import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Building2, HardHat, Sparkles, CheckCircle2, Clock, Shield, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const ServicesPage = () => {
  const residentialServices = [
    {
      title: 'Weekly / Bi-Weekly Cleaning',
      description: 'Regular maintenance cleaning to keep your home consistently fresh and tidy.',
      features: ['Dusting & vacuuming', 'Kitchen & bathroom cleaning', 'Floor mopping', 'Surface sanitization']
    },
    {
      title: 'Deep Cleaning',
      description: 'Thorough top-to-bottom cleaning for a complete refresh of your living space.',
      features: ['Inside appliances', 'Behind furniture', 'Baseboards & moldings', 'Light fixtures']
    },
    {
      title: 'Move-In / Move-Out',
      description: 'Comprehensive cleaning for property transitions, ensuring spaces are spotless.',
      features: ['Cabinet interiors', 'Closet cleaning', 'Window tracks', 'Full sanitization']
    },
    {
      title: 'Special Occasion',
      description: 'Preparing your home for guests or cleaning up after events and gatherings.',
      features: ['Pre-event preparation', 'Post-party cleanup', 'Holiday cleaning', 'Guest-ready service']
    }
  ];

  const constructionServices = [
    {
      title: 'Post-Construction Cleanup',
      description: 'Complete cleaning after construction or renovation projects of any scale.',
      features: ['Debris removal', 'Dust elimination', 'Surface polishing', 'Final inspection']
    },
    {
      title: 'Hotel Room Turnover',
      description: 'Large-scale room preparation for hotels and hospitality properties.',
      features: ['200+ room capacity', 'Quick turnaround', 'Quality assurance', 'Deadline compliance']
    },
    {
      title: 'Apartment Complex',
      description: 'Unit-by-unit cleaning for new developments and property management.',
      features: ['Move-in ready', 'Common areas', 'Consistent standards', 'Bulk scheduling']
    },
    {
      title: 'Commercial Spaces',
      description: 'Office buildings, retail spaces, and commercial property cleaning.',
      features: ['After-hours service', 'Minimal disruption', 'Custom schedules', 'Large footprints']
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Request Quote',
      description: 'Tell us about your space and cleaning needs through our simple form.'
    },
    {
      number: '02',
      title: 'Assessment',
      description: 'We evaluate your requirements and provide a detailed, transparent quote.'
    },
    {
      number: '03',
      title: 'Schedule',
      description: 'Choose a convenient time that works for you. We accommodate your schedule.'
    },
    {
      number: '04',
      title: 'Deliver',
      description: 'Our professional team arrives, cleans, and ensures your complete satisfaction.'
    }
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-brand-surface" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-4">
              Our Services
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-navy tracking-tight leading-tight mb-6">
              Professional Cleaning<br />
              <span className="text-brand-slate">For Every Space</span>
            </h1>
            <p className="font-body text-lg text-brand-charcoal leading-relaxed mb-8">
              From cozy apartments to massive construction sites, ACH delivers meticulous cleaning services tailored to your specific needs. Two distinct service lines, one commitment to excellence.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-navy" />
                <span className="font-body text-sm text-brand-charcoal">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand-navy" />
                <span className="font-body text-sm text-brand-charcoal">Flexible Scheduling</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-brand-navy" />
                <span className="font-body text-sm text-brand-charcoal">100% Satisfaction</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Residential Services */}
      <section className="section-padding bg-white" data-testid="residential-services">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-brand-navy rounded-xl flex items-center justify-center">
                  <Home className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-brand-accent font-body text-xs font-semibold uppercase tracking-widest">
                    Service Category
                  </p>
                  <h2 className="font-heading font-bold text-2xl text-brand-navy">
                    Residential Cleaning
                  </h2>
                </div>
              </div>
              <p className="font-body text-brand-charcoal leading-relaxed mb-6">
                From studio apartments to luxury estates, we bring the same level of care and attention to every home. Our residential services are designed to fit your lifestyle and schedule.
              </p>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1756199638047-6d6930280e37?w=800&q=80" 
                  alt="Clean modern home interior"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="space-y-6">
              {residentialServices.map((service, index) => (
                <div 
                  key={index}
                  className="bg-brand-surface rounded-2xl p-6 card-hover"
                  data-testid={`residential-service-${index}`}
                >
                  <h3 className="font-heading font-semibold text-lg text-brand-navy mb-2">
                    {service.title}
                  </h3>
                  <p className="font-body text-sm text-brand-charcoal mb-4">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-brand-navy flex-shrink-0" />
                        <span className="font-body text-xs text-brand-charcoal">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Construction Services */}
      <section className="section-padding bg-brand-surface" data-testid="construction-services">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="order-2 lg:order-1 space-y-6">
              {constructionServices.map((service, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 card-hover"
                  data-testid={`construction-service-${index}`}
                >
                  <h3 className="font-heading font-semibold text-lg text-brand-navy mb-2">
                    {service.title}
                  </h3>
                  <p className="font-body text-sm text-brand-charcoal mb-4">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-brand-navy flex-shrink-0" />
                        <span className="font-body text-xs text-brand-charcoal">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="order-1 lg:order-2 lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-brand-navy rounded-xl flex items-center justify-center">
                  <HardHat className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-brand-accent font-body text-xs font-semibold uppercase tracking-widest">
                    Service Category
                  </p>
                  <h2 className="font-heading font-bold text-2xl text-brand-navy">
                    Construction & Commercial
                  </h2>
                </div>
              </div>
              <p className="font-body text-brand-charcoal leading-relaxed mb-6">
                Our large-scale capabilities set us apart. Whether it's 200 hotel rooms or a new apartment complex, we have the team, equipment, and experience to deliver on time and on budget.
              </p>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1767793357572-7f282da502d3?w=800&q=80" 
                  alt="Construction site cleaning"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-brand-navy" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-slate-400 font-body text-sm font-semibold uppercase tracking-widest mb-3">
              How It Works
            </p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
              Simple 4-Step Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <span className="font-heading font-bold text-6xl text-slate-800 opacity-50 absolute -top-4 left-0">
                  {step.number}
                </span>
                <div className="pt-12">
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-slate-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-white" data-testid="service-areas">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-3">
              Coverage Area
            </p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-4">
              Serving the Greater Austin Area
            </h2>
            <p className="font-body text-brand-charcoal max-w-2xl mx-auto">
              We proudly serve Austin and surrounding communities with the same commitment to excellence, no matter where you're located.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Austin', 'West Lake Hills', 'Buda', 'Pflugerville', 
              'Hutto', 'Georgetown', 'Steiner Ranch', 'Leander', 'Cedar Park'
            ].map((area) => (
              <span 
                key={area}
                className="px-5 py-2 bg-brand-surface rounded-full font-body text-sm text-brand-charcoal hover:bg-brand-navy hover:text-white transition-colors cursor-default"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-surface" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <Sparkles className="w-12 h-12 text-brand-navy mx-auto mb-6" />
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-4">
            Ready for a Cleaner Space?
          </h2>
          <p className="font-body text-brand-charcoal text-lg mb-8">
            Get your free, no-obligation quote today. We'll respond within 24 hours.
          </p>
          <Link to="/#quote-form">
            <Button 
              className="bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full px-8 py-6 btn-shine"
              data-testid="services-cta-btn"
            >
              Get Your Free Quote
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
